import { Center, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { object } from 'yup';

import Modal from '../../../../../../../components/Modal';
import { AddressInput } from '../../../../../../../components/formComponents';
import Button from '../../../../../../../components/ui/buttons/Button';
import PrimaryButton from '../../../../../../../components/ui/buttons/PrimaryButton';
import { Country } from '../../../../../../../gql/generated/graphql';
import { getLeadByExternalIdQueryKey } from '../../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { getLeadByExternalIdPostAcceptanceChecklistQueryKey } from '../../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPostAcceptanceChecklist';
import { usePatchLeadByExternalIdData } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import { GetLeadByExternalId200 } from '../../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { PatchLeadByExternalIdDataMutationRequest } from '../../../../../../../services/marketplace/generated/kubb/models/PatchLeadByExternalIdData';
import {
  addressLine2Validation,
  addressLineValidation,
  cityValidation,
  stateValidation,
} from '../../../../../../../utils/validation/address';
import { zipValidation } from '../../../../../../../utils/validation/zipCodes';
import { formatPickupAddress } from '../utils';
import TaskSuccessState from './TaskSuccessState';

const editSchema = object({
  address_line: addressLineValidation,
  address_line_2: addressLine2Validation,
  city: cityValidation,
  state: stateValidation,
  zip: zipValidation,
});

// Field names follow AddressInput's Formik contract.
type AddressFormValues = {
  address_line: string;
  address_line_2: string;
  city: string;
  state: string;
  zip: string;
  county: string;
};

type Stage = 'confirm' | 'edit' | 'success';

type ConfirmAddressModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lead: GetLeadByExternalId200;
  externalId: string;
};

const buildPatchData = (values: AddressFormValues): PatchLeadByExternalIdDataMutationRequest => ({
  addressLine1: values.address_line.trim(),
  addressLine2: values.address_line_2.trim() || null,
  city: values.city.trim(),
  state: values.state,
  zip: values.zip.trim(),
  driverEvent: 'pickup_address_confirmed',
});

const ConfirmAddressModal = ({ isOpen, onClose, lead, externalId }: ConfirmAddressModalProps) => {
  const [stage, setStage] = useState<Stage>('confirm');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAddress, setSavedAddress] = useState('');
  const queryClient = useQueryClient();
  const { mutateAsync: patchLead, isPending } = usePatchLeadByExternalIdData();

  const hasCompleteAddress = Boolean(lead.addressLine1 && lead.city && lead.state && lead.zip);

  useEffect(() => {
    if (!isOpen) return;
    setStage(hasCompleteAddress ? 'confirm' : 'edit');
    setSaveError(null);
  }, [isOpen]);

  const saveErrorMessage = saveError && (
    <Text fontSize="sm" color="leaseEndRed" textAlign="center">
      {saveError}
    </Text>
  );

  const save = async (data: PatchLeadByExternalIdDataMutationRequest) => {
    setSaveError(null);
    try {
      await patchLead({ externalId, data });
      // Refetch overlaps the success screen instead of delaying it.
      queryClient.invalidateQueries({ queryKey: getLeadByExternalIdQueryKey({ externalId }) });
      queryClient.invalidateQueries({
        queryKey: getLeadByExternalIdPostAcceptanceChecklistQueryKey({ externalId }),
      });
      setSavedAddress(formatPickupAddress({ ...lead, ...data }));
      setStage('success');
    } catch {
      setSaveError("We couldn't save that. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm pickup address"
      titleFontSize="22px"
      titleFontWeight={700}
    >
      {stage === 'confirm' && (
        <VStack spacing={5} pb={4}>
          <Text textAlign="center" color="charcoal">
            A carrier will pick up the car here. Is this right?
          </Text>
          <Text fontSize="18px" fontWeight={700} textAlign="center" color="leaseEndBlue">
            {formatPickupAddress(lead)}
          </Text>
          {saveErrorMessage}
          <Center w="100%">
            <PrimaryButton
              loading={isPending}
              value="YES, THAT'S RIGHT"
              onClick={() => save({ driverEvent: 'pickup_address_confirmed' })}
            />
          </Center>
          <Button variant="link" onClick={() => setStage('edit')} isDisabled={isPending}>
            Something's wrong, edit address
          </Button>
        </VStack>
      )}

      {stage === 'edit' && (
        <Formik<AddressFormValues>
          initialValues={{
            address_line: lead.addressLine1 ?? '',
            address_line_2: lead.addressLine2 ?? '',
            city: lead.city ?? '',
            state: lead.state ?? '',
            zip: lead.zip ?? '',
            county: '',
          }}
          validationSchema={editSchema}
          validateOnChange={false}
          onSubmit={(values) => save(buildPatchData(values))}
        >
          {({ isSubmitting }) => (
            <Form>
              <VStack spacing={4} pb={4} align="stretch">
                <AddressInput dropLabel country={Country.Us} />
                {saveErrorMessage}
                <Center w="100%">
                  <PrimaryButton type="submit" loading={isSubmitting} value="SAVE ADDRESS" />
                </Center>
                {lead.addressLine1 && (
                  <Button
                    variant="link"
                    onClick={() => setStage('confirm')}
                    isDisabled={isSubmitting}
                  >
                    Back
                  </Button>
                )}
              </VStack>
            </Form>
          )}
        </Formik>
      )}

      {stage === 'success' && (
        <TaskSuccessState title="Pickup address confirmed" onClose={onClose}>
          <Text fontSize="14px" color="charcoal" textAlign="center">
            {savedAddress}
          </Text>
        </TaskSuccessState>
      )}
    </Modal>
  );
};

export default ConfirmAddressModal;
